import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strategicPillarService } from '@/lib/api/services/strategic-pillar'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  StrategicPillar,
  StrategicPillarDetail,
  StrategicPillarFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const strategicPillarKeys = {
  all: ['strategic-pillars'] as const,
  lists: () => [...strategicPillarKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: StrategicPillarFilterParams) =>
    [...strategicPillarKeys.lists(), params, filters] as const,
  searches: () => [...strategicPillarKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...strategicPillarKeys.searches(), searchTerm, params] as const,
  details: () => [...strategicPillarKeys.all, 'detail'] as const,
  detail: (id: string) => [...strategicPillarKeys.details(), id] as const,
}

// Hook to fetch all strategic pillars
export function useAllStrategicPillars() {
  return useQuery<ApiResponse<StrategicPillar[]>, Error>({
    queryKey: [...strategicPillarKeys.all, 'all'],
    queryFn: () => strategicPillarService.getAllStrategicPillars()
  })
}

// Hook to filter strategic pillars with pagination
export function useFilterStrategicPillars(
  params: PaginationParams,
  filters: StrategicPillarFilterParams
) {
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, filters),
    queryFn: () => strategicPillarService.getStrategicPillars(params, filters)
  })
}

// Hook to search strategic pillars with pagination
export function useSearchStrategicPillars(
  searchTerm: string,
  params: PaginationParams
) {
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.search(searchTerm, params),
    queryFn: () => strategicPillarService.searchStrategicPillars(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to fetch a single strategic pillar
export function useStrategicPillar(strategicPillarId?: string) {
  return useQuery<ApiResponse<StrategicPillarDetail>, Error>({
    queryKey: strategicPillarKeys.detail(strategicPillarId!),
    queryFn: () => strategicPillarService.getStrategicPillarById(strategicPillarId!),
    enabled: !!strategicPillarId
  })
}

// Hook to fetch landing page strategic pillars
export function useLandingPageStrategicPillars(
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) {
  const mergedFilters = { ...filters, landingPagePrioritiesOfAfsa: true }
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, mergedFilters),
    queryFn: () => strategicPillarService.getLandingPageStrategicPillars(params, filters)
  })
}

// Hook to fetch "Who We Are" strategic pillars
export function useWhoWeAreStrategicPillars(
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) {
  const mergedFilters = { ...filters, whoWeAre: true }
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, mergedFilters),
    queryFn: () => strategicPillarService.getWhoWeAreStrategicPillars(params, filters)
  })
}

// Hook to fetch countries strategic pillars
export function useCountriesStrategicPillars(
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) {
  const mergedFilters = { ...filters, countries: true }
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, mergedFilters),
    queryFn: () => strategicPillarService.getCountriesStrategicPillars(params, filters)
  })
}

// Hook to fetch history strategic pillars
export function useHistoryStrategicPillars(
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) {
  const mergedFilters = { ...filters, history: true }
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, mergedFilters),
    queryFn: () => strategicPillarService.getHistoryStrategicPillars(params, filters)
  })
}

// Hook to fetch member strategic pillars
export function useMemberStrategicPillars(
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) {
  const mergedFilters = { ...filters, member: true }
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, mergedFilters),
    queryFn: () => strategicPillarService.getMemberStrategicPillars(params, filters)
  })
}

// Hook to fetch published strategic pillars
export function usePublishedStrategicPillars(
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<StrategicPillar>>, Error>({
    queryKey: strategicPillarKeys.list(params, filters),
    queryFn: () => strategicPillarService.getPublishedStrategicPillars(params, filters)
  })
}

// Hook to increment view count for a strategic pillar
export function useIncrementStrategicPillarView() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (strategicPillarId: string) =>
      strategicPillarService.incrementView(strategicPillarId),
    onSuccess: (_, strategicPillarId) => {
      // Invalidate the detail query to refetch with updated view count
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.detail(strategicPillarId) })
    },
  })
}

/**
 * Hook to fetch strategic pillar highlights
 */
export function useStrategicPillarHighlights(type: string = 'HOME_PAGE_PRIORITIES_OF_AFSA') {
  return useQuery<ApiResponse<StrategicPillar[]>, Error>({
    queryKey: [...strategicPillarKeys.all, 'highlights', type],
    queryFn: () => strategicPillarService.getStrategicPillarHighlights(type),
    staleTime: 5 * 60 * 1000,
  })
}
